import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { PasteDefinitionResult, getPasteDefinitions } from 'stores/ContractPasteStoreAdapter';
import { ContractRequestDetailResult } from 'stores/ContractRequestStoreAdapter';
import { ContractDetailResult } from 'stores/ContractStoreAdapter';
import { MasterTypeResult, getMasterType } from 'stores/MasterTypeStoreAdapter';
import { successDefault } from 'utils/Toastify';

// --- Enum変換マップ ---
const CONTRACT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: '契約中',
  TERMINATED: '契約終了',
};

const REQUEST_TYPE_LABELS: Record<string, string> = {
  CREATE: '新規申請',
  CHANGE: '変更申請',
  TERMINATE: '解除申請',
};

const REQUEST_STATUS_LABELS: Record<string, string> = {
  ADJUSTING: '調整中',
  SUBMITTED: '申請中',
  IN_PROGRESS: '対応中',
  APPROVED: '承認',
  REJECTED: '否決',
  CANCELLED: '取消',
};

// master_type変換対象フィールド
const MASTER_TYPE_FIELDS: Record<string, string> = {
  prefectureId: 'prefecture',
  contractTypeId: 'contract_type',
  contractUnitId: 'contract_unit',
};

// enum変換対象フィールド
const ENUM_FIELDS: Record<string, Record<string, string>> = {
  status: CONTRACT_STATUS_LABELS,
  requestType: REQUEST_TYPE_LABELS,
  requestStatus: REQUEST_STATUS_LABELS,
};

// column_key → 実際のデータパスのマッピング（契約詳細APIのネスト構造に対応）
const CONTRACT_FIELD_PATH_MAP: Record<string, string> = {
  memberName: 'member.memberName',
  projectName: 'project.projectName',
  clientName: 'client.clientName',
  partnerName: 'partner.partnerName',
  managerName: 'manager.userName',
  managementId: 'managementId',
};

type ContractPasteButtonProps = {
  data: ContractDetailResult | ContractRequestDetailResult;
  dataType: 'contract' | 'contractRequest';
};

const ContractPasteButton: React.FC<ContractPasteButtonProps> = ({ data, dataType }) => {
  const [definitions, setDefinitions] = useState<PasteDefinitionResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewValues, setPreviewValues] = useState<string[]>([]);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  // master_type data for value resolution
  const [prefectures, setPrefectures] = useState<MasterTypeResult[]>([]);
  const [contractTypes, setContractTypes] = useState<MasterTypeResult[]>([]);
  const [contractUnits, setContractUnits] = useState<MasterTypeResult[]>([]);

  useEffect(() => {
    // Fetch paste definitions and master types in parallel
    Promise.all([
      getPasteDefinitions(),
      getMasterType('prefecture'),
      getMasterType('contract_type'),
      getMasterType('contract_unit'),
    ])
      .then(([pasteRes, prefs, ctypes, cunits]) => {
        setDefinitions(pasteRes.results);
        setPrefectures(prefs);
        setContractTypes(ctypes);
        setContractUnits(cunits);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const masterTypeMap = useMemo(() => {
    const map: Record<string, MasterTypeResult[]> = {
      prefecture: prefectures,
      contract_type: contractTypes,
      contract_unit: contractUnits,
    };
    return map;
  }, [prefectures, contractTypes, contractUnits]);

  const selectedDefinition = definitions[selectedIndex] || null;

  // Resolve a single column value from data
  const resolveValue = useCallback(
    (columnKey: string): string => {
      const rawData = data as Record<string, any>;

      // 1. 可変項目: contractItems.{item_key}
      if (columnKey.startsWith('contractItems.')) {
        const itemKey = columnKey.replace('contractItems.', '');
        if (dataType === 'contract') {
          const contractItems = rawData.contractItems as
            Record<string, { currentValues: string[]; pendingValues: string[] | null }> | undefined;
          if (contractItems && contractItems[itemKey]) {
            return (contractItems[itemKey].currentValues || []).join(',');
          }
        } else {
          const templateItems = rawData.templateItems as
            Record<string, { mainValues: string[]; subValues: string[] | null }> | undefined;
          if (templateItems && templateItems[itemKey]) {
            return (templateItems[itemKey].mainValues || []).join(',');
          }
        }
        return '';
      }

      // 2. enum変換対象
      if (ENUM_FIELDS[columnKey]) {
        const value = rawData[columnKey];
        if (value == null) return '';
        return ENUM_FIELDS[columnKey][value] || String(value);
      }

      // 3. master_type変換対象
      if (MASTER_TYPE_FIELDS[columnKey]) {
        const typeName = MASTER_TYPE_FIELDS[columnKey];
        const masterList = masterTypeMap[typeName] || [];

        // For contractRequest, get .main value
        if (dataType === 'contractRequest') {
          const field = rawData[columnKey];
          const code = field?.main ?? field;
          if (code == null) return '';
          const found = masterList.find((m) => m.typeCode === String(code));
          return found ? found.typeLabel : String(code);
        }
        // For contract, this field doesn't exist directly (uses object structure)
        // So return empty for contract-specific fields
        return '';
      }

      // 4. ドット区切りパス（配列内プロパティ or ネストオブジェクト）
      if (columnKey.includes('.')) {
        return resolveNestedValue(rawData, columnKey);
      }

      // 5. フラットフィールドがネスト構造にマッピングされている場合（契約詳細API対応）
      if (dataType === 'contract' && CONTRACT_FIELD_PATH_MAP[columnKey]) {
        const path = CONTRACT_FIELD_PATH_MAP[columnKey];
        const resolved = resolveNestedValue(rawData, path);
        if (resolved) return resolved;
      }

      // 6. フラットフィールド (with current/main handling)
      const fieldValue = rawData[columnKey];
      if (fieldValue == null) return '';

      // Object with current/pending (contract)
      if (dataType === 'contract' && typeof fieldValue === 'object' && 'current' in fieldValue) {
        const val = fieldValue.current;
        return formatOutputValue(val);
      }

      // Object with main/sub (contractRequest)
      if (
        dataType === 'contractRequest' &&
        typeof fieldValue === 'object' &&
        'main' in fieldValue
      ) {
        const val = fieldValue.main;
        return formatOutputValue(val);
      }

      return formatOutputValue(fieldValue);
    },
    [data, dataType, masterTypeMap],
  );

  // Resolve nested path like "applicants.userName" or "personInCharge.current.userName"
  const resolveNestedValue = (obj: Record<string, any>, path: string): string => {
    const parts = path.split('.');
    let current: any = obj;

    for (let i = 0; i < parts.length; i++) {
      if (current == null) return '';

      const part = parts[i];

      // If current value is an array, extract the property from each element
      if (Array.isArray(current)) {
        const remaining = parts.slice(i).join('.');
        const values = current
          .map((item) => {
            if (remaining.includes('.')) {
              return resolveNestedValue(item, remaining);
            }
            return item[remaining];
          })
          .filter((v) => v != null)
          .map(String);
        return values.join(',');
      }

      current = current[part];
    }

    if (current == null) return '';
    if (Array.isArray(current)) {
      return current.map(String).join(',');
    }
    return formatOutputValue(current);
  };

  // Format output value (boolean → 〇/×, null → empty, tab → space)
  const formatOutputValue = (val: any): string => {
    if (val == null) return '';
    if (typeof val === 'boolean') return val ? '〇' : '×';
    const str = String(val);
    // Replace tab characters with space
    return str.replace(/\t/g, ' ');
  };

  // Build preview data
  const buildPreview = useCallback(() => {
    if (!selectedDefinition) return;
    const values = selectedDefinition.columns.map((col) => resolveValue(col.columnKey));
    setPreviewValues(values);
  }, [selectedDefinition, resolveValue]);

  const handleButtonClick = () => {
    if (!selectedDefinition) return;
    buildPreview();
    setDialogOpen(true);
  };

  const handleMenuItemClick = (index: number) => {
    setSelectedIndex(index);
    setMenuOpen(false);
  };

  const handleCopy = () => {
    const tsv = previewValues.join('\t');
    navigator.clipboard.writeText(tsv).then(() => {
      successDefault('クリップボードにコピーしました');
    });
  };

  const handleCellEdit = (index: number, value: string) => {
    setPreviewValues((prev) => {
      const newArr = [...prev];
      newArr[index] = value;
      return newArr;
    });
  };

  // Disabled state
  const isDisabled = loading || definitions.length === 0;

  if (loading) {
    return (
      <Button
        size="small"
        variant="outlined"
        disabled
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
        }}
      >
        <CircularProgress size={16} sx={{ mr: 0.5 }} />
        ペースト
      </Button>
    );
  }

  if (definitions.length === 0) {
    return null;
  }

  return (
    <>
      <ButtonGroup ref={anchorRef} variant="outlined" size="small" aria-label="ペースト定義選択">
        <Button
          onClick={handleButtonClick}
          disabled={isDisabled}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {selectedDefinition?.name || 'ペースト'}
        </Button>
        <Button
          onClick={() => setMenuOpen((prev) => !prev)}
          disabled={isDisabled}
          aria-controls={menuOpen ? 'paste-split-menu' : undefined}
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-haspopup="menu"
          sx={{ px: 0.5, minWidth: 'auto' }}
        >
          <ArrowDropDownIcon fontSize="small" />
        </Button>
      </ButtonGroup>

      <Popper
        open={menuOpen}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                <MenuList id="paste-split-menu" autoFocusItem>
                  {definitions.map((def, index) => (
                    <MenuItem
                      key={def.pasteDefinitionId}
                      selected={index === selectedIndex}
                      onClick={() => handleMenuItemClick(index)}
                    >
                      <Typography variant="body2">{def.name}</Typography>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* プレビューダイアログ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          elevation: 0,
          sx: { borderRadius: 3, border: 1, borderColor: 'divider', minHeight: '40vh' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              ペーストプレビュー: {selectedDefinition?.name}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="クリップボードにコピー">
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => setDialogOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 2 }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {selectedDefinition?.columns.map((col, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        bgcolor: 'action.hover',
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <Tooltip title={col.displayName} enterDelay={300}>
                        <span>{col.displayName}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {previewValues.map((val, i) => (
                    <EditableCell key={i} value={val} onChange={(v) => handleCellEdit(i, v)} />
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </>
  );
};

// --- Editable Cell Component ---
type EditableCellProps = {
  value: string;
  onChange: (value: string) => void;
};

const EditableCell: React.FC<EditableCellProps> = ({ value, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue !== value) {
      onChange(editValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      onChange(editValue);
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value);
    }
  };

  if (isEditing) {
    return (
      <TableCell sx={{ p: 0.5 }}>
        <TextField
          size="small"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          fullWidth
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              fontSize: '0.8rem',
            },
            '& .MuiOutlinedInput-input': {
              py: 0.5,
              px: 1,
            },
          }}
        />
      </TableCell>
    );
  }

  return (
    <TableCell
      onClick={() => setIsEditing(true)}
      sx={{
        cursor: 'pointer',
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        maxWidth: 160,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Tooltip title={value || ''} enterDelay={300}>
        <span>{value || '—'}</span>
      </Tooltip>
    </TableCell>
  );
};

export default ContractPasteButton;
